import { Button } from 'antd';
import React from 'react';
import { createSelector } from 'reselect';

import style from './index.less';

type ReactChild = JSX.Element | string;

interface TitleProps {
  children?: ReactChild;
}

const Title = ({ children }: TitleProps) => <h4 className={style.title}>{children}</h4>;
const Content = ({ children }: TitleProps) => <p className={style.content}>{children}</p>;

type ButtonBoxDataItem = {
  onClick?: () => void;
  label: string;
};

interface ButtonBoxPorps {
  data: Array<ButtonBoxDataItem>;
}

type AntdBtnType = 'default' | 'link' | 'ghost' | 'primary' | 'dashed' | 'danger';

const addTtnType = createSelector([(data: Array<ButtonBoxDataItem>) => data], data => {
  const length = data.length;
  const btnsType: Array<AntdBtnType> = ['link', 'default'];
  const singleBtnType: Array<AntdBtnType> = ['default'];
  if (length <= 1) {
    return data.map((val, index) => ({
      ...val,
      btnType: singleBtnType[index]
    }));
  }

  return data.map((val, index) => ({
    ...val,
    btnType: btnsType[index]
  }));
});

const ButtonBox = ({ data }: ButtonBoxPorps): JSX.Element => {
  const buttonData = addTtnType(data);
  return (
    <div>
      {buttonData.map(val => {
        const { label, onClick, btnType } = val;
        return (
          <Button key={label} type={btnType} onClick={onClick} size="small">
            {label}
          </Button>
        );
      })}
    </div>
  );
};

type TextData = {
  title?: ReactChild;
  content?: ReactChild;
};

interface CommoncComponentProps {
  pageText: ReactChild;
  textData?: TextData;
  btnData: Array<ButtonBoxDataItem>;
}
const CommonComponent = (props: CommoncComponentProps): JSX.Element => {
  const { pageText, textData, btnData } = props;
  return (
    <div className={style['guide-modal']}>
      <div>
        <Title>{textData?.title}</Title>
        <Content>{textData?.content}</Content>
      </div>
      <div className={style['guide-modal-footer']}>
        <span>{pageText}</span>
        <ButtonBox data={btnData} />
      </div>
    </div>
  );
};

interface ModalProps {
  index: number;
  goTo: (page: number) => void;
  pageText: ReactChild;
  handleComplete?: () => void;
}

const widgetCopyCodeData = {
  title: '接入网页插件',
  content: '复制代码，将此代码嵌入到你网站页面body标签之前即可启用来鼓'
};
const CopyWidgetCode = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={widgetCopyCodeData} {...rest} />;
};

const widgetApiData = {
  title: '查看开发文档',
  content: '需要你的技术人员查看和操作'
};
const WidgetApi = (props: ModalProps): JSX.Element => {
  const { index, pageText, goTo, handleComplete } = props;
  const { title, content } = widgetApiData;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '完成',
      onClick: () => {
        if (handleComplete) {
          handleComplete();
        }
      }
    }
  ];
  return (
    <div className={style['guide-modal']}>
      <div>
        <Title>{title}</Title>
        <Content>{content}</Content>
        <span className={style.link} onClick={() => goTo(index + 1)}>
          其他接入方式
        </span>
      </div>
      <div className={style['guide-modal-footer']}>
        <span>{pageText}</span>
        <div>
          <ButtonBox data={btnData} />
        </div>
      </div>
    </div>
  );
};

const chatLinkData = {
  title: '接入聊天链接',
  content: '聊天链接可以接入网站、微信公众号、小程序和APP等多种渠道'
};
const ChatLink = (props: ModalProps): JSX.Element => {
  const { index, goTo, handleComplete, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '完成',
      onClick: () => {
        if (handleComplete) {
          handleComplete();
        }
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={chatLinkData} {...rest} />;
};

const teamInfoData = {
  title: '团队信息',
  content: '配置你的团队信息'
};
const TeamInfo = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={teamInfoData} {...rest} />;
};

const editTeamInfoData = {
  title: '团队信息',
  content: '点击【编辑】，完善你的团队信息'
};
const EditTeamInfo = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={editTeamInfoData} {...rest} />;
};

const groupInfoData = {
  title: '客服与分组',
  content: '在这里，添加客服账号，管理你的团队成员'
};
const GroupInfo = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={groupInfoData} {...rest} />;
};

const addGroupData = {
  title: '添加客服分组',
  content: '添加一个分组来管理你的客服'
};
const AddGroup = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={addGroupData} {...rest} />;
};

const addAccountData = {
  title: '添加客服账号',
  content: '邀请你的团队成员，一起接待对话'
};
const AddAccount = (props: ModalProps): JSX.Element => {
  const { index, goTo, handleComplete, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '完成',
      onClick: () => {
        if (handleComplete) {
          handleComplete();
        }
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={addAccountData} {...rest} />;
};

const chatSettingData = {
  title: '对话页设置',
  content: '设置你的客服欢迎语和昵称'
};
const ChatSetting = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={chatSettingData} {...rest} />;
};

const chatWelcomeData = {
  title: '客服个人欢迎语',
  content: '修改你的个人欢迎语，功能启用后个人欢迎语会在开启对话时自动发送'
};
const ChatWelcome = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={chatWelcomeData} {...rest} />;
};

const chatCardData = {
  title: '客服昵称',
  content: '设置的客服名称，会在访客端的聊天窗口显示。快去取一个与众不同的名字吧！'
};
const ChatCard = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={chatCardData} {...rest} />;
};

const personalSettingData = {
  title: '账户信息',
  content: '鼠标移入【导航-头像】，在这里修改你的头像和个人账户信息'
};
const PersonalSetting = (props: ModalProps): JSX.Element => {
  const { index, goTo, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '下一步',
      onClick: () => {
        goTo(index + 1);
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={personalSettingData} {...rest} />;
};

const agentStatusData = {
  title: '在线状态',
  content: '把你的在线状态修改为【在线】 ，就可以开始接待对话啦~'
};
const AgentStatus = (props: ModalProps): JSX.Element => {
  const { index, goTo, handleComplete, ...rest } = props;
  const btnData = [
    {
      label: '上一步',
      onClick: () => {
        goTo(index - 1);
      }
    },
    {
      label: '完成',
      onClick: () => {
        if (handleComplete) {
          handleComplete();
        }
      }
    }
  ];
  return <CommonComponent btnData={btnData} textData={agentStatusData} {...rest} />;
};

export {
  // 第一步
  CopyWidgetCode,
  WidgetApi,
  ChatLink,
  // 第二步
  TeamInfo,
  EditTeamInfo,
  GroupInfo,
  AddGroup,
  AddAccount,
  // 第三步
  ChatSetting,
  ChatWelcome,
  ChatCard,
  PersonalSetting,
  AgentStatus
};

export default CommonComponent;
